import * as React from 'react';
import { connect } from 'react-redux';
import { find, remove, sortBy, debounce } from 'lodash';
import * as validator from 'validator';
import {
  Button,
  IconButton,
  Form,
  AdditionalDriverCreateForm,
  DriverList,
  Pagination,
  Icon,
  Box,
  Tooltip,
} from '../';
import {
  getDrivers,
  getCompanyDrivers,
  showError,
  clearErrors,
} from '../../redux/actions/drivers';
import PAGINATE from '../../data/paginate';

interface Props {
  booking: any;
  drivers: Array<any>;
  companyDrivers?: Array<any>;
  handleUpdate: Function;
  isDisabled: boolean;
  dispatch: Function;
  list: boolean;
  companies: Array<any>;
  disableFirstDriverRemove: boolean;
  isFetching: boolean;
}

interface State {
  drivers: Array<any>;
  companyDriversList?: Array<any>;
  isLoadingDrivers: boolean;
  isAddingDriver: boolean;
  page: number;
  search: string;
  driverList: Array<any>;
  addNewDriver: boolean;
}

class BookingDrivers extends React.Component<Props, State> {
  constructor(props) {
    super(props);
    this.state = {
      drivers: this.props.booking.drivers,
      companyDriversList: this.props.companyDrivers,
      isLoadingDrivers: true,
      isAddingDriver: false,
      page: 0,
      search: '',
      driverList: this.props.drivers,
      addNewDriver: false,
    };
  }

  componentWillReceiveProps({ isFetching, drivers }: Props) {
    if (!isFetching) {
      this.setState({
        driverList: drivers,
      });
    }
  }

  handleUpdate(drivers) {
    this.setState({ drivers });
    this.props.handleUpdate(drivers);
  }

  showError = (error: string) => {
    this.props.dispatch(showError(error));
  };

  /**
   * Validate creating a new driver
   */
  handleCreateValidation = (data) => {
    this.props.dispatch(clearErrors());
    if (
      validator.isEmpty(data.first_name) ||
      validator.isEmpty(data.last_name)
    ) {
      this.showError("Please specify the driver's full name");
      return false;
    }
    if (validator.isEmpty(String(data.phone_number))) {
      this.showError('Please specify a phone number');
      return false;
    }
    if (!validator.isEmpty(data.email) && !validator.isEmail(data.email)) {
      this.showError('Please specify a valid email address');
      return false;
    }
    if (validator.isEmpty(data.occupation)) {
      this.showError('Please specify an occupation');
      return false;
    }
    if (validator.isEmpty(data.driving_license_number)) {
      this.showError('Please specify a driving licence number');
      return false;
    }
    if (
      !validator.isEmpty(data.driving_license_number) &&
      !validator.isLength(data.driving_license_number, { min: 16, max: 16 })
    ) {
      this.showError(
        'Please make sure the driving licence number is 16 characters long',
      );
      return false;
    }
    if (
      !validator.isEmpty(data.expiry_date) &&
      validator.isBefore(data.expiry_date, new Date().toString())
    ) {
      this.showError("Please specify an expiry date that hasn't expired");
      return false;
    }
    return data;
  };

  /**
   * Render the driver search for the driver list
   */
  private renderDriverSearch = () => (
    <>
      <div className="columns">
        <div className="column is-9">
          <Form
            type="search"
            fields={[{ name: 'search', type: 'text' }]}
            disabled={this.state.addNewDriver}
            hasButton={false}
            handleChange={debounce(({ search }) => {
              this.setState({ search, page: 0 }, () =>
                this.props.dispatch(getDrivers(undefined, 0, search)),
              );
            }, 200)}
          />
        </div>
        <div className="column is-3">
          <IconButton
            icon="plus"
            handleClick={() =>
              this.setState({ addNewDriver: !this.state.addNewDriver })
            }
            className={`is-fullwidth is-primary ${
              this.state.addNewDriver ? '' : 'is-outlined'
            }`}
          >
            New Driver
          </IconButton>
        </div>
      </div>
    </>
  );

  render() {
    return (
      <>
        {/* Assigned drivers */}
        <DriverList
          items={this.state.drivers}
          showJoinDate={false}
          showLicence={true}
          showReputation={true}
          handleRemove={
            this.props.isDisabled
              ? undefined
              : (driver, index) => {
                  if (this.props.disableFirstDriverRemove && index === 0) {
                    window.alert(
                      `${driver.name} can't be removed as they are the lead driver`,
                    );
                    return;
                  }
                  if (
                    !this.props.isDisabled &&
                    window.confirm(
                      `Really remove ${driver.name} from the booking?`,
                    )
                  ) {
                    const drivers = this.state.drivers;
                    remove(drivers, { id: driver.id });
                    this.handleUpdate(drivers);
                  }
                }
          }
          hideSearch
        />

        {/* Recommended Company Drivers */}
        {this.props.booking.company_id &&
          this.state.companyDriversList &&
          this.state.companyDriversList.length > 0 && (
            <>
              <Box>
                <p>
                  <Tooltip text="Choose recommended Company drivers">
                    <strong>Recommended Drivers:</strong>
                    <Icon icon="question-circle" />
                  </Tooltip>
                </p>
                <DriverList
                  items={this.state.companyDriversList.slice(
                    0,
                    PAGINATE.companyDrivers.MAX,
                  )}
                  showJoinDate={false}
                  showLicence={true}
                  showReputation={true}
                  hideSearch
                  noAnimation
                  handleClick={(driver) => {
                    const drivers = this.state.drivers;
                    const selectedDriver = find(drivers, {
                      id: driver.id,
                    });
                    if (!selectedDriver) {
                      this.setState({ isAddingDriver: false });
                      drivers.push(driver);
                    } else if (find(drivers, { id: driver.id })) {
                      window.alert(
                        `${driver.name} is already on this booking.`,
                      );
                    }
                    this.handleUpdate(drivers);
                  }}
                />
                {this.state.companyDriversList.length > 0 && (
                  <Pagination
                    page={this.state.page}
                    lastPage={
                      this.state.companyDriversList.length <=
                      PAGINATE.companyDrivers.MAX
                    }
                    handleUpdate={async (page) => {
                      this.setState({ page });
                      const drivers = await this.props.dispatch(
                        getCompanyDrivers(this.props.booking.company_id, page),
                      );
                      this.setState({ companyDriversList: drivers.data });
                    }}
                  />
                )}
              </Box>
            </>
          )}

        {/* Dropdown for adding new drivers */}
        {!this.props.isDisabled && (
          <>
            <div className="buttons">
              <Button
                className={`is-fullwidth is-${
                  this.state.isAddingDriver && !this.state.isLoadingDrivers
                    ? 'primary'
                    : 'default'
                }${
                  this.state.isAddingDriver && this.state.isLoadingDrivers
                    ? ' is-loading'
                    : ''
                }`}
                handleClick={() => {
                  this.setState(
                    {
                      isAddingDriver: !this.state.isAddingDriver,
                      page: 0,
                      search: '',
                    },
                    () => {
                      if (this.state.isAddingDriver) {
                        this.setState({ isLoadingDrivers: true }, async () => {
                          await this.props.dispatch(getDrivers());
                          this.setState({ isLoadingDrivers: false });
                        });
                      }
                    },
                  );
                }}
              >
                <span>Add Additional Drivers</span>
                <Icon
                  icon={`chevron-${
                    this.state.isAddingDriver && !this.state.isLoadingDrivers
                      ? 'down'
                      : 'right'
                  }`}
                />
              </Button>
            </div>
            {this.state.isAddingDriver && !this.state.isLoadingDrivers && (
              <Box>
                <this.renderDriverSearch />
                {!this.state.addNewDriver && this.state.search !== '' && (
                  <>
                    <DriverList
                      hideSearch
                      noAnimation
                      showReputation={true}
                      items={this.state.driverList.slice(
                        0,
                        PAGINATE.drivers.MAX,
                      )}
                      showJoinDate={false}
                      handleClick={(driver) => {
                        const drivers = this.state.drivers;
                        const foundDriver = find(drivers, {
                          id: driver.id,
                        });
                        if (!foundDriver) {
                          this.setState({ isAddingDriver: false });
                          drivers.push(driver);
                        } else if (find(drivers, { id: driver.id })) {
                          window.alert(
                            `${driver.name} is already on this booking.`,
                          );
                        }
                        this.handleUpdate(drivers);
                      }}
                    />
                    {this.props.drivers.length > 0 && (
                      <Pagination
                        page={this.state.page}
                        lastPage={
                          this.props.drivers.length <= PAGINATE.drivers.MAX
                        }
                        handleUpdate={(page) => {
                          this.setState({ page });
                          this.props.dispatch(
                            getDrivers(undefined, page, this.state.search),
                          );
                        }}
                      />
                    )}
                  </>
                )}
              </Box>
            )}
            {this.state.isAddingDriver &&
              !this.state.isLoadingDrivers &&
              this.state.addNewDriver && (
                <AdditionalDriverCreateForm
                  onUpdate={(response) => {
                    if (!response.error) {
                      const drivers = this.state.drivers;
                      this.setState({ isAddingDriver: false });
                      drivers.push(response.data);
                      this.handleUpdate(drivers);
                      window.scrollTo(0, 0);
                    }
                  }}
                />
              )}
          </>
        )}
      </>
    );
  }
}

const mapStateToProps = ({
  drivers: { drivers, isFetching },
  companies: { companies },
}) => ({
  isFetching,
  drivers: sortBy(drivers, 'name'),
  companies: companies.map(({ id, name }) => ({
    name,
    value: id,
  })),
});

export default connect(mapStateToProps)(BookingDrivers);
